import { Box, CircularProgress } from "@mui/material";
import { useContext, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getAPIAsync, postAPIAsync } from "../api/apiCalls";
import Provider from "../api/provider";
import { inIframe, isNullOrEmpty, MAX_RECORD, SELLER_SITE_PATH, WEBSITE_PATH, windowPostMessage } from "../common/common";
import { resource } from "../common/resource";
import { CreateHeader } from "../components/headers";
import { FollowersNullSet } from "../components/nullSets/chatNullset";
import InboxSkeleton from "../components/skeletonLoaders/inboxSkeleton";
import StoreListCards, { SetStoreData } from "../components/storeCardList";
import { StoreDetailsContext } from "../contexts/entityDetailsContext";
import { FollowingAccountsResponse, FollowingEntityInfo } from "../models/inbox";

const StoreFollowers = () => {
  const { storeDetails, setStoreDetails } = useContext<any>(StoreDetailsContext);
  const [nullSet, setNullSet] = useState<boolean>(false);
  const [loaderPage, setLoaderPage] = useState(true);
  const [followers, setFollowers] = useState<FollowingEntityInfo[]>([]);
  const [pageNo, setPageNo] = useState<number>(0);
  const [isAjax, setIsAjax] = useState<boolean>(true);
  const [loader, setLoader] = useState<boolean>(false);
  const [totalFollowingCount, setTotalFollowingCount] = useState<number>(0);
  const [searchParam] = useSearchParams();

  const storecodenum: any = useRef<string>();

  //#region useeffect
  useEffect(() => {
    if (!inIframe()) {
      window.location.href = WEBSITE_PATH;
      return;
    }
    storecodenum.current = encodeURIComponent(searchParam.get("storeId"));
    window.addEventListener("message", handleWindowMessageReceived);
    return () => {
      window.removeEventListener("message", handleWindowMessageReceived);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isNullOrEmpty(storeDetails)) {
      windowPostMessage({ type: "getstoredetails" }, SELLER_SITE_PATH);
    } else {
      storeDetails && storeDetails.scnum && getUserFollowersList(pageNo);
      updateFollowersCount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeDetails]);

  useEffect(() => {
    pageNo !== 0 && storeDetails && storeDetails.scnum && getUserFollowersList(pageNo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNo]);

  //#endregion

  //#region Api calls
  const updateFollowersCount = () => {
    postAPIAsync(Provider.URLS.BASE_URL, Provider.API_URLS.UpdateStoreNewFollowersCount, { scnum: storeDetails.scnum }, () => {
      windowPostMessage({ type: "updateNotification" }, SELLER_SITE_PATH);
    });
  };

  const getUserFollowersList = async (pageNoVal: number) => {
    getAPIAsync(
      Provider.URLS.BASE_URL,
      Provider.API_URLS.StoreFollowers,
      {
        ucnum: storeDetails.ucnum,
        scnum: storeDetails.scnum,
        isAllPostsCount: totalFollowingCount === 0,
        pageSize: MAX_RECORD,
        pageNo: pageNoVal,
      },
      async (response: any) => {
        setLoaderPage(false);
        const data: FollowingAccountsResponse[] = response.data;
        let followingListCount: number = 0;
        if (data) {
          if (data[0]?.totalFollowers && data[0]?.totalFollowers > 0) {
            setTotalFollowingCount(data[0]?.totalFollowers);
            followingListCount = data[0]?.totalFollowers;
          } else {
            followingListCount = totalFollowingCount;
          }

          if (data[0]?.followingEntityInfo) {
            const existingData = [...followers, ...data[0].followingEntityInfo];
            setFollowers(existingData);
            if (existingData && existingData.at(-1)) {
              const lastFollowedTimestamp = existingData?.at(-1)?.followedTstamp;
              if (lastFollowedTimestamp) {
                window.history.replaceState("", "", `${window.location.origin}/store-followers?storeId=${storeDetails.scnum}&sessionId=${storeDetails.sessionID}&since=${new Date(lastFollowedTimestamp).getTime()}`);
              }
            }
            if (existingData.length >= followingListCount) {
              setLoaderAndAjax(false, false);
              return;
            }
          }

          if (followingListCount === 0) {
            setNullSet(true);
          }
        } else {
          setNullSet(true);
        }
        setLoaderAndAjax(false, true);
      },
      async (e: any) => {
        setLoaderPage(false);
        setLoaderAndAjax(false, false);
        isNullOrEmpty(e.data) && windowPostMessage({ type: "snackMsg", snackMsg: resource.error }, SELLER_SITE_PATH);
      }
    );
  };
  //#endregion

  //#region Function calls
  const handleWindowMessageReceived = (event: any) => {
    if (event.origin && event.origin.includes(SELLER_SITE_PATH.slice(0, -1))) {
      const data = event.data;
      if (data.type === "storedetails") {
        SetStoreData(storecodenum.current, data.storeDetails, searchParam.get("sessionId"), setStoreDetails);
      }
    }
  };

  const onScrollEvent = (e: any) => {
    const $eTarget = e.currentTarget;
    if (!isAjax || !($eTarget.scrollHeight - $eTarget.scrollTop - 10 <= $eTarget.clientHeight)) {
      return;
    }
    setLoaderAndAjax(true, false);
    setPageNo(pageNo + MAX_RECORD);
  };

  const setLoaderAndAjax = (loader: boolean, ajax: boolean) => {
    setLoader(loader);
    setIsAjax(ajax);
  };

  //#endregion

  return (
    <Box className="inboxWindow flex-column pad-top-gutter">
      <CreateHeader title="New followers" isTextBox={false} noBack={true} />
      {nullSet ? (
        <Box className="inboxContentContainerEmpty">
          <FollowersNullSet header="New store followers" body={`${resource.newFollowerStoreNullSet}`} />
        </Box>
      ) : loaderPage ? (
        <InboxSkeleton />
      ) : (
        <Box className="inboxContentContainer pad-l-r-gutter" onScroll={onScrollEvent}>
          <Box className="flex-column">{followers.length > 0 && <StoreListCards data={followers} type="newfollowers" secondaryActionWidth="94px"/>}</Box>
          {loader && (
            <Box className="flex-center ht-64" sx={{ m: 2 }}>
              <CircularProgress className="box-24" />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default StoreFollowers;
